import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { VendingMachineService, SuburbInfo } from '../../api/v1/api_pb';
import { useClient } from '../../connect_api';
import { VendingMachineInfo } from '../../api/v1/api_pb';
import { Helmet } from 'react-helmet';
import VendingMachineMap from './vending_machine_map';
import { haversineDistance, formatDistance } from '../../utils/geo';

export default function VendingMachineMapPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const client = useClient(VendingMachineService);

    // Get machine code from URL parameter (more reliable than location.state on iOS Safari)
    const machineCodeFromUrl = searchParams.get('machine');

    const [vendingMachineInformations, setVendingMachineInformations] = useState<VendingMachineInfo[]>([]);
    const [selectedMachineForMap, setSelectedMachineForMap] = useState<VendingMachineInfo | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [suburbSuggestions, setSuburbSuggestions] = useState<SuburbInfo[]>([]);
    const [selectedSuburb, setSelectedSuburb] = useState<SuburbInfo | null>(null);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null);
    const [mapReady, setMapReady] = useState(false);
    const [showLocationCard, setShowLocationCard] = useState(false);

    const getAllVendingMachine = useCallback(async () => {
        try {
            const response = await client.listVendingMachines({});
            setVendingMachineInformations(response.vendingMachines);

            // If a machine code was passed via URL parameter, select it
            if (machineCodeFromUrl) {
                const machine = response.vendingMachines.find(
                    vm => vm.machineCode === machineCodeFromUrl
                );
                if (machine) {
                    setSelectedMachineForMap(machine);
                }
            }
        } catch (error) {
            navigate(`/`, { replace: true });
        }
    }, [client, navigate, machineCodeFromUrl]);

    useEffect(() => {
        getAllVendingMachine();
    }, [getAllVendingMachine]);

    // Show location card when map is ready and there's a selected machine
    useEffect(() => {
        if (mapReady && selectedMachineForMap) {
            setShowLocationCard(true);
        }
    }, [mapReady, selectedMachineForMap]);

    // Search suburbs with typeahead
    const searchSuburbs = useCallback(async (query: string) => {
        if (!query || query.length < 2) {
            setSuburbSuggestions([]);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        try {
            const response = await client.searchSuburbs({ query, limit: 10 });
            setSuburbSuggestions(response.suburbs);
            setShowSuggestions(true);
        } catch (error) {
            console.error('Failed to search suburbs:', error);
            setSuburbSuggestions([]);
        } finally {
            setIsSearching(false);
        }
    }, [client]);

    // Handle search query change
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setSearchQuery(query);
        searchSuburbs(query);
    };

    // Handle suburb selection
    const handleSuburbSelect = (suburb: SuburbInfo) => {
        setSelectedSuburb(suburb);
        setSearchQuery(`${suburb.locality} ${suburb.postcode}`);
        setShowSuggestions(false);
        // Set map center to the selected suburb
        setMapCenter({ lat: suburb.latitude, lng: suburb.longitude });
    };

    // Handle input blur with delay to allow clicks on suggestions
    const handleInputBlur = () => {
        // Delay hiding to allow click events on suggestions to fire first
        setTimeout(() => {
            setShowSuggestions(false);
        }, 200);
    };

    const handleCollectFromHere = (vendingMachine: VendingMachineInfo) => {
        navigate('/machine-detail/' + vendingMachine.machineCode);
    };

    const handleClose = () => {
        navigate('/find-machine');
    };

    const handleMapReady = () => {
        setMapReady(true);
    };

    const handleSelectVendingMachineOnMap = (vendingMachine: VendingMachineInfo) => {
        // Update URL parameter when clicking markers (iOS Safari reliable)
        setSearchParams({ machine: vendingMachine.machineCode });
        setSelectedMachineForMap(vendingMachine);
    };

    // Get formatted distance based on selected suburb
    const getDistance = (vendingMachine: VendingMachineInfo): string => {
        if (selectedSuburb && vendingMachine.latitude && vendingMachine.longitude) {
            const distanceKm = haversineDistance(
                selectedSuburb.latitude,
                selectedSuburb.longitude,
                vendingMachine.latitude,
                vendingMachine.longitude
            );
            return formatDistance(distanceKm);
        }
        return '';
    };

    return (
        <>
            <Helmet>
                <title>Vending Machine Map - MyTest</title>
            </Helmet>
            <div className="map-view-fullscreen">
                <div className="map-search-bar">
                    <div className="search-input-container">
                        <div className="search-input-wrapper">
                            {isSearching ? (
                                <svg className="search-icon spinner" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="8" cy="8" r="6" stroke="#400093" strokeWidth="2" strokeLinecap="round" strokeDasharray="30 10" />
                                </svg>
                            ) : (
                                <svg className="search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7 13C10.3137 13 13 10.3137 13 7C13 3.68629 10.3137 1 7 1C3.68629 1 1 3.68629 1 7C1 10.3137 3.68629 13 7 13Z" stroke="#400093" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M15 15L11.5 11.5" stroke="#400093" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            )}
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={handleSearchChange}
                                onFocus={() => setShowSuggestions(true)}
                                onBlur={handleInputBlur}
                                placeholder="To find nearby machines, enter your suburb or postcode (e.g. Parramatta or 2150)"
                            />
                        </div>
                        {showSuggestions && suburbSuggestions.length > 0 && (
                            <div className="suburb-suggestions">
                                {suburbSuggestions.map((suburb, index) => (
                                    <div
                                        key={index}
                                        className="suburb-suggestion-item"
                                        onClick={() => handleSuburbSelect(suburb)}
                                    >
                                        <strong>{suburb.locality}</strong> {suburb.postcode}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <button className="close-button" onClick={handleClose}>×</button>
                </div>
                {!showSuggestions && (
                    <div style={{ padding: '0 16px', marginTop: '12px', position: 'absolute', top: '76px', left: 0, right: 0, zIndex: 1002 }}>
                        <button
                            onClick={handleClose}
                            style={{
                                width: 'calc(100% - 32px)',
                                margin: '0 16px',
                                padding: '12px 24px',
                                backgroundColor: '#B2F100',
                                color: '#066448',
                                border: 'none',
                                borderRadius: '50px',
                                fontSize: '16px',
                                fontWeight: 600,
                                fontFamily: 'Inter',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                boxShadow: '4px 2px 6.2px 0px rgba(0, 0, 0, 0.2)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#9DD400';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = '#B2F100';
                            }}
                        >
                            Back to list view
                        </button>
                    </div>
                )}

                <div className="map-container">
                    <VendingMachineMap
                        vendingMachines={vendingMachineInformations}
                        selectedVendingMachine={selectedMachineForMap}
                        onSelectVendingMachine={handleSelectVendingMachineOnMap}
                        customCenter={mapCenter}
                        onMapReady={handleMapReady}
                    />
                </div>

                {showLocationCard && selectedMachineForMap && (
                    <div className="map-location-card">
                        <div className="location-card">
                            <div className="location-header">
                                <h2 className="location-name">{selectedMachineForMap.name}</h2>
                                <p className="location-distance">{getDistance(selectedMachineForMap)}</p>
                            </div>
                            <p className="location-address">{selectedMachineForMap.address}</p>
                            <button
                                className="collect-button"
                                onClick={() => handleCollectFromHere(selectedMachineForMap)}
                            >
                                Collect from here
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
